import { Component, Input } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

interface LocationTab {
  id: string;
  label: string;
  /** Query used to build the Google Maps embed URL */
  mapQuery: string;
  address: string[];
  phone: string;
  /** First tab (institutional) keeps the rounded card; level tabs are flat */
  isInstitution: boolean;
  /** Level tabs show this image (web_image, falling back to main_image) instead of the map */
  image: string | null;
}

@Component({
  selector: 'app-sblock-location',
  templateUrl: './location.component.html',
  styleUrls: ['./location.component.scss']
})
export class SblockLocationComponent {
  eyebrow = 'Nuestra ubicación en mapas';
  title = '¿Dónde encontrarnos?';

  /**
   * Institutional location data from GET /public/web-page → `general`
   * ({ latitude, longitude, address, phone, city, country }). Feeds the first tab.
   */
  @Input()
  set general(value: any) {
    this._general = value;
    this.rebuildTabs();
  }
  get general(): any {
    return this._general;
  }
  private _general: any = null;

  /**
   * Educational levels from GET /public/levels. Each one becomes a tab whose map
   * and contact card use the level's web_* / contact fields (falling back to the
   * institutional data when empty).
   */
  @Input()
  set levels(value: any[]) {
    this._levels = Array.isArray(value) ? value : [];
    this.rebuildTabs();
  }
  get levels(): any[] {
    return this._levels;
  }
  private _levels: any[] = [];

  /** Built from `general` + `levels`; rebuilt only when those inputs change. */
  tabs: LocationTab[] = [];

  activeIndex = 0;

  // Static fallbacks used when the API data is empty
  private readonly fallbackAddress = ['Blvd. Gálvez 1978', 'S3000 ACY, Santa Fe'];
  private readonly fallbackPhone = '0342 - 4538271';
  private readonly fallbackQuery = 'Blvd. Gálvez 1978, Santa Fe, Argentina';

  /** Cache of sanitized embed URLs, keyed by query, so we don't rebuild on every CD cycle. */
  private safeUrls: { [query: string]: SafeResourceUrl } = {};

  constructor(private sanitizer: DomSanitizer) {
    this.rebuildTabs();
  }

  get activeTab(): LocationTab {
    return this.tabs[this.activeIndex] || this.tabs[0];
  }

  setActive(index: number): void {
    this.activeIndex = index;
  }

  getSlideStyles(): { [k: string]: string } {
    const total = this.tabs.length || 1;
    const widthPercent = 100 / total;
    return {
      width: `${widthPercent}%`,
      left: `${widthPercent * this.activeIndex}%`
    };
  }

  mapUrl(): SafeResourceUrl {
    const query = this.activeTab.mapQuery;
    if (!this.safeUrls[query]) {
      const url = `https://maps.google.com/maps?q=${encodeURIComponent(query)}&t=&z=15&ie=UTF8&iwloc=&output=embed`;
      this.safeUrls[query] = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    }
    return this.safeUrls[query];
  }

  private rebuildTabs(): void {
    const tabs: LocationTab[] = [this.buildInstitutionTab()];
    this._levels.forEach(level => tabs.push(this.buildLevelTab(level)));
    this.tabs = tabs;
    if (this.activeIndex >= tabs.length) {
      this.activeIndex = 0;
    }
  }

  /** Institutional tab fed by `general`, with static fallbacks */
  private buildInstitutionTab(): LocationTab {
    const g = this._general;
    let mapQuery = this.fallbackQuery;
    let address = this.fallbackAddress;
    let phone = this.fallbackPhone;

    if (g) {
      if (g.latitude != null && g.longitude != null) {
        mapQuery = `${g.latitude},${g.longitude}`;
      } else {
        const parts = [g.address, g.city, g.country].filter(Boolean);
        if (parts.length) {
          mapQuery = parts.join(', ');
        }
      }
      const lines = [g.address, [g.city, g.country].filter(Boolean).join(', ')].filter(Boolean);
      if (lines.length) {
        address = lines;
      }
      if (g.phone) {
        phone = g.phone;
      }
    }

    return { id: 'institucion', label: 'Institución', mapQuery, address, phone, isInstitution: true, image: null };
  }

  /** Level tab fed by the level's web_* / contact fields, falling back to the institution */
  private buildLevelTab(level: any): LocationTab {
    const institution = this.buildInstitutionTab();
    const addressLines = [level?.web_address, level?.web_address_instructions].filter(Boolean);
    const address = addressLines.length ? addressLines : institution.address;
    const phone = level?.web_phone || level?.contact?.phone || institution.phone;
    const mapQuery = level?.web_address || institution.mapQuery;

    return {
      id: level?.slug || `nivel-${level?.id ?? ''}`,
      label: level?.name || 'Nivel',
      mapQuery,
      address,
      phone,
      isInstitution: false,
      image: level?.web_image || level?.main_image || null
    };
  }
}
